import * as React from 'react'
import { FC } from 'react'
import { Helmet } from 'react-helmet'
import { FluidImageType } from '../Image/Image.types'
import DottedRectangles from '../DottedRectangles/DottedRectangles'
import { cls } from '../../utils/utils'
import { Parallelepiped } from '../Parallelepiped/Parallelepiped'
import { BackgroundColorType, TextColors } from '../../utils/constants'
import { IParallelepipedColor } from '../Parallelepiped/Parallelepiped.types'
import ReadyToTalk from '../Section/components/ReadyToTalk/ReadyToTalk'
import Tags from '../Tags/Tags'
import SearchBar from '../SearchBar/SearchBar'
import * as styles from './MainSection.module.scss'
import { IFirstSectionProps, ISecondSectionProps } from './MainSection.types'

const parallelepipedColors: Record<BackgroundColorType, IParallelepipedColor> = {
  white: 'grey',
  grey: 'white',
  dark: 'black',
}

const FirstSection: FC<IFirstSectionProps> = ({ children, image, rectanglesColor = 'grey', main, shifted, className }) => {
  let mainBg = ''

  if (typeof image === 'string') {
    mainBg = `url(${image})`
  } else {
    if (image?.childImageSharp) {
      mainBg = `url(${(image.childImageSharp as FluidImageType)?.fluid.src})`
    } else if (image?.publicURL) {
      mainBg = `url(${image.publicURL})`
    }
  }

  const internalStyles = {
    backgroundImage: `${mainBg} `,
  }

  return (
    <section className={cls(styles.firstSection, main && styles.main, className)} style={internalStyles}>
      {shifted && (
        <Helmet>
          <body className={styles.hideOverflow} />
        </Helmet>
      )}
      <div className="main-container">
        <div className={styles.container}>{children}</div>
        <DottedRectangles
          color={rectanglesColor}
          className={cls(!main ? styles.rectangles : styles.mainRectangles, shifted ? styles.shifted : '')}
        />
      </div>
    </section>
  )
}

const SecondSection: FC<ISecondSectionProps> = ({
  children,
  anchor,
  dontShowRectangles = false,
  hideBottomPadding = false,
  dontShowParallelepiped = false,
  parallelepipedColor,
  smallPadding,
  readyToTalkStyle,
  hidePaddingForReady = false,
  readyToTalkText,
  tags = null,
  selectedTag = '',
  searchQuery = '',
  backgroundColor,
  showSearch = false,
  root,
}) => (
  <section
    id={anchor}
    className={cls(
      styles.cancelCollapse,
      styles.secondSection,
      hideBottomPadding ? styles.dontShowPadding : styles.showUpperPadding,
      !dontShowParallelepiped && styles.addBottomPadding,
      backgroundColor === 'dark' && styles.darkSection,
      backgroundColor === 'white' && styles.whiteSection
    )}
  >
    {!dontShowParallelepiped && <Parallelepiped color={parallelepipedColors[parallelepipedColor || 'white']} />}
    <div className="main-container">
      {showSearch && tags && root && (
        <div className={styles.searchBlockContainer}>
          <div className={styles.searchBlockItemsContainer}>
            <SearchBar searchQuery={searchQuery} selectedTag={selectedTag} root={root} />
            <div className={styles.tagsContainer}>
              <Tags tags={tags} className={styles.tag} selectedTag={selectedTag} searchQuery={searchQuery} root={root} />
            </div>
          </div>
        </div>
      )}

      <div
        className={cls(
          styles.container,
          (tags ?? false) && styles.smallContainer,
          selectedTag && styles.hideUpperPadding,
          !hideBottomPadding && !smallPadding && styles.showLowerPadding,
          !hidePaddingForReady && !smallPadding && styles.showLowerPadding,
          smallPadding && styles.showSmallPadding
        )}
      >
        {children}
      </div>
      {readyToTalkStyle && (
        <ReadyToTalk
          colorTitle={backgroundColor === 'dark' ? TextColors.WHITE : TextColors.DARK}
          btnStyle={readyToTalkStyle}
          readyToTalkText={readyToTalkText}
        />
      )}
      {!dontShowRectangles && <DottedRectangles color="grey" />}
    </div>
  </section>
)

export const MainSectionParts = {
  FirstSection,
  SecondSection,
}

const MainSection: FC<{ className?: string }> = (props) => (
  <div className={cls(styles.mainBackground, props.className)}>{props.children}</div>
)
export default MainSection
